import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { CheckIcon, SearchIcon, TrendingUpIcon, TargetIcon, FileTextIcon, BarChartIcon, LinkIcon, ZapIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function SEOService() {
  const features = [{
    icon: SearchIcon,
    title: 'Keyword Research',
    description: 'Target the right keywords your customers are searching for'
  }, {
    icon: FileTextIcon,
    title: 'On-Page SEO',
    description: 'Optimize content, meta tags, and site structure'
  }, {
    icon: LinkIcon,
    title: 'Link Building',
    description: 'Build quality backlinks to boost authority'
  }, {
    icon: TargetIcon,
    title: 'Local SEO',
    description: 'Dominate local search results in your area'
  }, {
    icon: BarChartIcon,
    title: 'Analytics Setup',
    description: 'Track performance with Google Analytics & Search Console'
  }, {
    icon: TrendingUpIcon,
    title: 'Ongoing Optimization',
    description: 'Continuous improvements to maintain rankings'
  }, {
    icon: ZapIcon,
    title: 'Performance Optimisation',
    description: 'Speed improvements and technical enhancements'
  }];
  const benefits = ['Increase organic traffic to your website', 'Rank higher in Google search results', 'Reach customers actively searching for you', 'Build long-term sustainable growth', 'Better ROI than paid advertising', 'Establish authority in your industry'];
  const process = [{
    step: '01',
    title: 'Audit & Research',
    description: 'We analyse your current SEO performance, research competitors, and identify opportunities for improvement.'
  }, {
    step: '02',
    title: 'Strategy Development',
    description: 'We create a customised SEO strategy based on your business goals, target audience, and competitive landscape.'
  }, {
    step: '03',
    title: 'Implementation',
    description: 'We optimise your website content, technical elements, and build quality backlinks to improve rankings.'
  }, {
    step: '04',
    title: 'Monitor & Report',
    description: 'We track your rankings, traffic, and conversions, providing monthly reports on progress and insights.'
  }, {
    step: '05',
    title: 'Ongoing Optimisation',
    description: 'SEO is ongoing. We continuously refine your strategy based on performance data and algorithm updates.'
  }];
  return <>
      <Helmet data-id="element-2126">
        <title data-id="element-2127">
          SEO Services UK | Search Engine Optimization – Bright Pixxel
        </title>
        <meta name="description" content="Professional SEO services to boost your rankings and drive organic traffic. Keyword research, on-page optimization, and link building." data-id="element-2128" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-2129">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2130">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2131">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5
          }} className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center" data-id="element-2132">
              <SearchIcon size={40} className="text-[#0DC3DC]" data-id="element-2133" />
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl md:text-5xl font-bold mb-6 text-white" data-id="element-2134">
              SEO Services
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg md:text-xl text-white/90 mb-8" data-id="element-2135">
              Improve your search engine rankings with technical SEO, keyword
              optimisation, and content strategy that drives organic traffic to
              your website.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2136">
              <Link to="/pricing" className="bg-[#0DC3DC] hover:bg-[#0DC3DC]/90 text-white px-8 py-3 rounded-lg font-medium transition-colors shadow-lg" data-id="element-2137">
                Get a Free SEO Audit
              </Link>
              <Link to="/portfolio" className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-[#0C1B26] px-8 py-3 rounded-lg font-medium transition-colors" data-id="element-2138">
                View Results
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Features Grid */}
      <section className="py-16 bg-white dark:bg-[#1E1E1E]" data-id="element-2139">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2140">
          <div className="max-w-6xl mx-auto" data-id="element-2141">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-center mb-12" data-id="element-2142">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2143">
                Complete SEO Solutions
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300" data-id="element-2144">
                Everything you need to dominate search results
              </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2145">
              {features.map((feature, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-[#F9FAFB] dark:bg-[#212C34] p-6 rounded-xl hover:shadow-lg transition-shadow" data-id="element-2146">
                  <div className="w-12 h-12 bg-[#0DC3DC]/20 rounded-lg flex items-center justify-center mb-4" data-id="element-2147">
                    <feature.icon size={24} className="text-[#0DC3DC]" data-id="element-2148" />
                  </div>
                  <h3 className="text-xl font-bold mb-2" data-id="element-2149">{feature.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300" data-id="element-2150">
                    {feature.description}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* Benefits Section with Image */}
      <section className="py-16 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-2151">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2152">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-2153">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-2154">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-2155">
                Why SEO Matters for Your Business
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-8" data-id="element-2156">
                SEO is one of the most cost-effective ways to attract customers
                who are actively looking for your products or services.
              </p>
              <div className="space-y-4" data-id="element-2157">
                {benefits.map((benefit, index) => <motion.div key={index} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: index * 0.1
              }} className="flex items-center gap-3" data-id="element-2158">
                    <div className="w-6 h-6 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center flex-shrink-0" data-id="element-2159">
                      <CheckIcon size={14} className="text-[#0DC3DC]" data-id="element-2160" />
                    </div>
                    <span className="text-gray-700 dark:text-gray-300" data-id="element-2161">
                      {benefit}
                    </span>
                  </motion.div>)}
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="rounded-xl overflow-hidden shadow-xl" data-id="element-2162">
              <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="SEO analytics dashboard showing search rankings and traffic growth - Bright Pixxel Studios professional SEO services" className="w-full h-auto" loading="lazy" decoding="async" data-id="element-2163" />
            </motion.div>
          </div>
        </div>
      </section>
      {/* Process Section */}
      <section className="py-16 bg-white dark:bg-[#1E1E1E]" data-id="element-2164">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2165">
          <div className="max-w-4xl mx-auto" data-id="element-2166">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-2167">
              Our SEO Process
            </motion.h2>
            <div className="space-y-8" data-id="element-2168">
              {process.map((item, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="flex gap-6" data-id="element-2169">
                  <div className="flex-shrink-0" data-id="element-2170">
                    <div className="w-16 h-16 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center" data-id="element-2171">
                      <span className="text-2xl font-bold text-[#0DC3DC]" data-id="element-2172">
                        {item.step}
                      </span>
                    </div>
                  </div>
                  <div className="flex-grow" data-id="element-2173">
                    <h3 className="text-xl font-bold mb-2" data-id="element-2174">{item.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300" data-id="element-2175">
                      {item.description}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* Stats Section */}
      <section className="py-16 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-2176">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2177">
          <div className="max-w-5xl mx-auto" data-id="element-2178">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-2179">
              Real Results from Our SEO Services
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2180">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="text-center" data-id="element-2181">
                <div className="text-5xl font-bold text-[#0DC3DC] mb-2" data-id="element-2182">
                  +250%
                </div>
                <p className="text-gray-600 dark:text-gray-300" data-id="element-2183">
                  Average traffic increase within 6 months
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="text-center" data-id="element-2184">
                <div className="text-5xl font-bold text-[#0DC3DC] mb-2" data-id="element-2185">
                  #1-3
                </div>
                <p className="text-gray-600 dark:text-gray-300" data-id="element-2186">
                  Average ranking position for target keywords
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="text-center" data-id="element-2187">
                <div className="text-5xl font-bold text-[#0DC3DC] mb-2" data-id="element-2188">
                  +180%
                </div>
                <p className="text-gray-600 dark:text-gray-300" data-id="element-2189">
                  Average increase in qualified leads
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-2190">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2191">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2192">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-6 text-white" data-id="element-2193">
              Ready to Boost Your Rankings?
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-lg text-white/90 mb-8" data-id="element-2194">
              Get a free SEO audit and discover how we can help you rank higher
              in Google and attract more customers.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-2195">
              <a href="mailto:info@bright-pixxel.com?subject=New%20Enquiry%20-%20SEO%20Services" className="inline-block bg-[#0DC3DC] hover:bg-[#0DC3DC]/90 text-white px-8 py-4 rounded-lg font-bold transition-colors shadow-lg text-lg" data-id="element-2196">
                Get Your Free SEO Audit
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </>;
}